/* global localStorage */
import { createSelector, createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";

export interface AuthUser {
  email: string;
  name: string;
  roles: string[];
  isUbuildAdmin: boolean;
  acknowledgedAt: string | null;
}

export interface SessionPayload {
  idToken: string;
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
  user: AuthUser;
}

export interface TokenRefreshPayload {
  idToken: string;
  accessToken: string;
  refreshToken?: string;
  expiresIn: number;
}

interface AuthState {
  idToken: string | null;
  accessToken: string | null;
  refreshToken: string | null;
  expiresAt: number | null;
  user: AuthUser | null;
  authenticated: boolean;
  authChecked: boolean;
  accessDenied: boolean;
}

const storageKeys = ["id_token", "access_token", "refresh_token", "sda_expires_at", "sda_user"];

function readInitialState(): AuthState {
  if (typeof localStorage === "undefined") {
    return {
      idToken: null,
      accessToken: null,
      refreshToken: null,
      expiresAt: null,
      user: null,
      authenticated: false,
      authChecked: true,
      accessDenied: false,
    };
  }

  const idToken = localStorage.getItem("id_token");
  let user: AuthUser | null = null;
  try {
    const storedUser = localStorage.getItem("sda_user");
    user = storedUser ? JSON.parse(storedUser) : null;
  } catch (_error) {
    user = null;
  }

  return {
    idToken,
    accessToken: localStorage.getItem("access_token"),
    refreshToken: localStorage.getItem("refresh_token"),
    expiresAt: Number(localStorage.getItem("sda_expires_at")) || null,
    user,
    authenticated: Boolean(idToken),
    authChecked: !idToken,
    accessDenied: false,
  };
}

function persistState(state: AuthState): void {
  if (typeof localStorage === "undefined") return;
  if (state.idToken) localStorage.setItem("id_token", state.idToken);
  if (state.accessToken) localStorage.setItem("access_token", state.accessToken);
  if (state.refreshToken) localStorage.setItem("refresh_token", state.refreshToken);
  if (state.expiresAt) localStorage.setItem("sda_expires_at", String(state.expiresAt));
  if (state.user) localStorage.setItem("sda_user", JSON.stringify(state.user));
}

function clearStoredState(): void {
  if (typeof localStorage === "undefined") return;
  storageKeys.forEach((key) => localStorage.removeItem(key));
}

const initialState = readInitialState();

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    setSession(state, action: PayloadAction<SessionPayload>) {
      state.idToken = action.payload.idToken;
      state.accessToken = action.payload.accessToken;
      state.refreshToken = action.payload.refreshToken;
      state.expiresAt = Date.now() + action.payload.expiresIn * 1000;
      state.user = action.payload.user;
      state.authenticated = true;
      state.authChecked = true;
      state.accessDenied = false;
      persistState(state);
    },
    updateTokens(state, action: PayloadAction<TokenRefreshPayload>) {
      state.idToken = action.payload.idToken;
      state.accessToken = action.payload.accessToken;
      state.refreshToken = action.payload.refreshToken || state.refreshToken;
      state.expiresAt = Date.now() + action.payload.expiresIn * 1000;
      state.authenticated = true;
      state.authChecked = true;
      persistState(state);
    },
    clearSession(state) {
      state.idToken = null;
      state.accessToken = null;
      state.refreshToken = null;
      state.expiresAt = null;
      state.user = null;
      state.authenticated = false;
      state.authChecked = true;
      state.accessDenied = false;
      clearStoredState();
    },
    setAccessDenied(state) {
      state.idToken = null;
      state.accessToken = null;
      state.refreshToken = null;
      state.expiresAt = null;
      state.user = null;
      state.authenticated = false;
      state.authChecked = true;
      state.accessDenied = true;
      clearStoredState();
    },
    clearAccessDenied(state) {
      state.accessDenied = false;
    },
    markAuthChecked(state) {
      state.authChecked = true;
    },
  },
});

const selectAuth = (state) => state.auth;

export const selectAuthenticated = createSelector(selectAuth, (auth) => auth.authenticated);
export const selectIdToken = createSelector(selectAuth, (auth) => auth.idToken);
export const selectRefreshToken = createSelector(selectAuth, (auth) => auth.refreshToken);
export const selectUser = createSelector(selectAuth, (auth) => auth.user);
export const selectAuthChecked = createSelector(selectAuth, (auth) => auth.authChecked);
export const selectAccessDenied = createSelector(selectAuth, (auth) => auth.accessDenied);
export const { setSession, updateTokens, clearSession, setAccessDenied, clearAccessDenied, markAuthChecked } =
  authSlice.actions;
export default authSlice.reducer;
